import { Kbd } from "@components/Kbd";
import { SWITCH_USER_TOKENS } from "@components/keyboardShortcuts";
import { useUser } from "@context/UserContext";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faCamera,
  faChevronDown,
  faKeyboard,
  faMicrophone,
  faUser,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  cn,
  Dropdown,
  type DropdownItem,
} from "@scribenote/brian-design-system";
import { useNavigate } from "@tanstack/react-router";

const initials = (name: string) =>
  name
    .replace(/^Dr\.\s*/, "")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

interface MenuAction {
  id: string;
  label: string;
  icon: IconDefinition;
  tokens: string[];
  testId: string;
  run: () => void;
}

/** Log out lives on the access-code screen that Switch user drops back to. */
export const UserMenu = ({
  onOpenShortcuts,
}: {
  onOpenShortcuts: () => void;
}) => {
  const { currentUser, switchUser } = useUser();
  const navigate = useNavigate();

  const actions: MenuAction[] = [
    {
      id: "switch-user",
      label: "Switch user",
      icon: faUser,
      tokens: SWITCH_USER_TOKENS,
      testId: "header-switch-user",
      run: switchUser,
    },
    {
      id: "shortcuts",
      label: "Keyboard shortcuts",
      icon: faKeyboard,
      tokens: ["?"],
      testId: "header-keyboard-shortcuts",
      run: onOpenShortcuts,
    },
    {
      id: "voiceprint",
      label: "My voice",
      icon: faMicrophone,
      tokens: [],
      testId: "header-voiceprint",
      run: () => void navigate({ to: "/voiceprint" }),
    },
    {
      id: "reference-photo",
      label: "My reference photos",
      icon: faCamera,
      tokens: [],
      testId: "header-reference-photo",
      run: () => void navigate({ to: "/reference-photo" }),
    },
  ];

  const items: DropdownItem[] = actions.map((action) => ({
    id: action.id,
    label: action.label,
    icon: (
      <FontAwesomeIcon
        icon={action.icon}
        aria-hidden="true"
        className="text-annotation"
      />
    ),
    trailing: <Kbd tokens={action.tokens} className="ml-auto" />,
    dividerAfter: action.id === "switch-user",
    rowProps: {
      // `id`, not `label`: display text could later interpolate PHI.
      "data-dd-action-name": `User menu: ${action.id}`,
      "data-testid": action.testId,
    },
  }));

  return (
    <div className="shrink-0">
      <Dropdown
        items={items}
        onSelect={(id) => actions.find((action) => action.id === id)?.run()}
        placement="bottom-end"
        className="w-[17rem]"
        header={
          <div className="flex flex-col gap-1 py-1" data-dd-privacy="hidden">
            <span className="text-annotation-caps text-annotation">
              Working as
            </span>
            <span className="break-words font-display text-base font-semibold text-ink">
              {currentUser?.displayName ?? "Account"}
            </span>
            {currentUser && (
              <span
                data-testid="header-user-role"
                className="text-sm text-annotation"
              >
                {currentUser.role}
              </span>
            )}
          </div>
        }
        trigger={(triggerProps) => (
          <button
            type="button"
            {...triggerProps}
            // Fixed, or RUM would name the action after the user's name.
            data-dd-action-name="User menu"
            data-testid="header-user-menu"
            aria-label={currentUser?.displayName ?? "Account"}
            className={cn(
              "flex h-10 max-w-60 cursor-pointer items-center gap-2.5 rounded-sheet px-2 text-left text-ink",
              "transition-colors duration-150 ease-paper hover:bg-smudge motion-reduce:transition-none",
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
            )}
          >
            <span
              aria-hidden="true"
              className="grid size-8 shrink-0 place-items-center rounded-full bg-wash-flag font-display text-sm font-medium text-ink"
            >
              {initials(currentUser?.displayName ?? "?")}
            </span>
            <span className="min-w-0">
              <span
                data-testid="header-user-name"
                className="block truncate font-display text-sm leading-4 font-semibold"
              >
                {currentUser?.displayName ?? "Account"}
              </span>
              {currentUser && (
                <span className="block truncate text-xs leading-4 text-annotation">
                  {currentUser.role}
                </span>
              )}
            </span>
            <FontAwesomeIcon
              icon={faChevronDown}
              aria-hidden="true"
              className={cn(
                "shrink-0 text-[0.625rem] text-annotation transition-transform duration-150 ease-paper motion-reduce:transition-none",
                triggerProps["aria-expanded"] && "rotate-180",
              )}
            />
          </button>
        )}
      />
    </div>
  );
};
